import { useQueryClient } from "@tanstack/react-query";
import { Form, Input, Modal, Switch } from "antd";
import { useEffect } from "react";
import type { Boat } from "../api-models";
import { useApi } from "../helpers/useApi";

interface BoatFormModalProps {
  open: boolean;
  /** The register entry being edited, or null to register a new boat. */
  boat: Boat | null;
  onCancel: () => void;
  /** Receives the saved entry — the Boat Note builder uses it to select what it just added. */
  onSaved: (boat: Boat) => void;
}

// The one place a boat is written, shared by the Boats master page and the Boat Note
// builder: a manifest is raised from the quayside, where a boat that is not in the
// register yet is a normal thing to hit, so the same form has to be reachable from both.
export function BoatFormModal({
  open,
  boat,
  onCancel,
  onSaved,
}: BoatFormModalProps) {
  const { request, loading } = useApi();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(boat ?? { active: true });
  }, [open, boat, form]);

  async function submit() {
    const values = await form.validateFields();
    const saved = boat
      ? await request<Boat>({
          method: "PUT",
          url: `/boats/${boat.id}`,
          data: values,
        })
      : await request<Boat>({ method: "POST", url: "/boats", data: values });
    queryClient.invalidateQueries({ queryKey: ["boats"] });
    onSaved(saved);
  }

  return (
    <Modal
      title={boat ? `Edit ${boat.name}` : "New boat"}
      open={open}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={loading}
      okText={boat ? "Save boat" : "Add boat"}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Boat name"
          rules={[{ required: true, message: "Name the boat." }]}
        >
          <Input placeholder="e.g. MV Dhoni" />
        </Form.Item>
        <Form.Item
          name="contact"
          label="Boat contact"
          rules={[
            { required: true, message: "Provide a contact for the boat." },
            { pattern: /^\d{7}$/, message: "Enter a 7-digit phone number." },
          ]}
        >
          <Input placeholder="Captain's phone number" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
        {/* Retirement is an edit, not a delete: issued manifests still point at the boat. */}
        {boat && (
          <Form.Item name="active" label="In service" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
